import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookMedical,
  faArrowLeft,
  faArrowRight,
  faEnvelopeCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./Forgotpassword.module.css";
import logoBg from "../static/logobgr.png";
import clinicLogo from "../static/logo.png";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const API_BASE = "/api";

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/patientauth/forgot-password`,
        { email }
      );
      if (response.data.status) {
        toast.success("Recovery link dispatched to your email!");
        setEmailSent(true);
      } else {
        toast.error(
          response.data.message || "Unable to find an account with that email."
        );
      }
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error(
        error.response?.data?.message ||
          "Error sending recovery instructions. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      {/* ─── 1. LEFT COLUMN (The Visual Anchor - Desktop Only) ───────── */}
      <div className={styles.leftCol}>
        <div className={styles.leftOverlay} />

        <div className={styles.leftContentTop}>
          <Link to="/" className={styles.brandLogo}>
            <img src={logoBg} alt="ReformMe Logo" className={styles.leftLogoImg} />
            <span>ReformMe Healthcare</span>
          </Link>
        </div>

        <div className={styles.leftContentBottom}>
          <div className={styles.quoteWrap}>
            <p className={styles.quoteText}>
              “Security and peace of mind go hand in hand. Easily restore your
              account access to resume your personalized care plan.”
            </p>
            <p className={styles.quoteAuthor}>
              Encrypted Patient Portal • Gurugram
            </p>
          </div>
        </div>
      </div>

      {/* ─── 2. RIGHT COLUMN (The Form Container) ────────────────────── */}
      <div className={styles.rightCol}>
        <div className={styles.formCard}>
          {/* Back Navigation Button */}
          <Link to="/login" className={styles.backNavBtn}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Sign In</span>
          </Link>

          {/* Conditional Rendering based on emailSent State */}
          {!emailSent ? (
            <>
              <div className={styles.formHeader}>
                <Link to="/" className={styles.formBrandLogo}>
                  <img
                    src={clinicLogo}
                    alt="ReformMe Healthcare"
                    className={styles.clinicLogoImg}
                  />
                  <div className={styles.brandTextStack}>
                    <span className={styles.brandTextTop}>ReformMe</span>
                    <span className={styles.brandTextBottom}>Healthcare</span>
                  </div>
                </Link>
                <h1 className={styles.headline}>Reset Password</h1>
                <p className={styles.subheadline}>
                  Enter your verified email address below and we will dispatch a
                  secure recovery link to your inbox.
                </p>
              </div>

              <form onSubmit={handleRequestReset} className={styles.form}>
                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.label}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    placeholder="patient@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={styles.input}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={styles.submitBtn}
                >
                  <span>{loading ? "Sending..." : "Send Recovery Link"}</span>
                  {!loading && <FontAwesomeIcon icon={faArrowRight} />}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className={styles.formHeader}>
                <div style={{ marginBottom: "0.5rem", color: "#2d6a4f", fontSize: "2rem" }}>
                  <FontAwesomeIcon icon={faEnvelopeCircleCheck} />
                </div>
                <h1 className={styles.headline}>Check Your Inbox</h1>
                <p className={styles.subheadline}>
                  We have dispatched secure recovery instructions to{" "}
                  <strong style={{ color: "#1e293b" }}>{email}</strong>.
                </p>
              </div>

              <div className={styles.successBox}>
                <p className={styles.successText}>
                  Please click the link inside the email to establish a new password.
                  The link remains valid for 30 minutes. Be sure to check your spam folder
                  if it does not arrive shortly.
                </p>
              </div>

              <div style={{ textAlign: "center", marginTop: "0.5rem" }}>
                <button
                  type="button"
                  onClick={() => setEmailSent(false)}
                  className={styles.retryBtn}
                >
                  Try another email address
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;